import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject, combineLatest, Observable, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

export interface ScreenDetails {
  height: number;
  width: number;
}

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class ScreenService {
  public currentScreenHeight$: BehaviorSubject<number> = new BehaviorSubject(window.outerHeight);
  public currentScreenWidth$: BehaviorSubject<number> = new BehaviorSubject(window.outerWidth);

  private resizeCallback: (event: any) => void = (event) => {
    this.currentScreenHeight$.next(event.target.outerHeight);
    this.currentScreenWidth$.next(event.target.outerWidth);
  }

  constructor(@Inject(PLATFORM_ID) public platformId: object) {
    window.addEventListener('resize', this.resizeCallback, {passive: true});
  }

  private findPaddings(elem: HTMLElement | Element): any {
    const styles: CSSStyleDeclaration = window.getComputedStyle(elem);
    return {
      pt: parseFloat(styles?.paddingTop) || 0,
      pb: parseFloat(styles?.paddingBottom) || 0,
      pl: parseFloat(styles?.paddingLeft) || 0,
      pr: parseFloat(styles?.paddingRight) || 0,
    }
  }

  private detectElemPaddings(target: string, closetsTarget?: string): any {
    if (isPlatformBrowser(this.platformId)) {
      const targetElem: HTMLElement = document.querySelector(target);
      const closestElem = targetElem.closest(closetsTarget);

      if (closestElem) {
        return this.findPaddings(closestElem)
      }

      return this.findPaddings(targetElem);
    }

  }

  getCurrentScreenRes(): ScreenDetails {
    return {
      height: this.currentScreenHeight$.value,
      width: this.currentScreenWidth$.value,
    };
  }

  getCurrentScreenRes$(): Observable<ScreenDetails> {
    return combineLatest([this.currentScreenWidth$.asObservable(), this.currentScreenHeight$.asObservable()])
      .pipe(
        untilDestroyed(this),
        map(([width, height]) => ({
          width,
          height,
        }))
      )
  }

  getCurrentScreenHeight$(): Observable<number> {
    return this.currentScreenHeight$.asObservable();
  }

  getCurrentScreenWidth$(relative?: { targetCheck: string, elemCheck?: string}): Observable<number> {
    if (relative) {
      return this.currentScreenWidth$.asObservable().pipe(
        switchMap((width) => of({width, paddings: this.detectElemPaddings(relative.targetCheck, relative.elemCheck)})),
        map(({width, paddings}) => width - paddings.pl - paddings.pl),
      );
    }
    return this.currentScreenWidth$.asObservable().pipe();
  }
}
